import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  CollectionReference,
  DocumentData,
  deleteField,
  documentId,
  limit,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Category, SubCategory } from '../types/category';
import { clearCategoryFromInventory } from './inventoryService';

// التحقق من توفر خدمة Firestore
const ensureFirestore = () => {
  if (!db) {
    throw new Error('خدمة Firestore غير متوفرة');
  }
  return db;
};

// مراجع المجموعات - سيتم إنشاؤها عند الطلب لضمان توفر db
const getCategoriesCollection = (): CollectionReference<DocumentData> => {
  const firestore = ensureFirestore();
  return collection(firestore, 'categories');
};

// إضافة فئة جديدة
export const addCategory = async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const categoriesCollection = getCategoriesCollection();

    // تحديد ترتيب الفئة الجديدة بناءً على عدد الفئات الموجودة من نفس النوع
    let newOrder = 0;

    try {
      // إذا كانت فئة فرعية، ابحث عن أعلى ترتيب في الفئات الفرعية تحت نفس الفئة الرئيسية
      if (category.parentId) {
        const subcats = await getSubcategories(category.parentId);
        if (subcats.length > 0) {
          // البحث عن أعلى ترتيب موجود وإضافة 1
          const maxOrder = Math.max(...subcats.map(cat => cat.order || 0));
          newOrder = maxOrder + 1;
        }
      } else {
        // إذا كانت فئة رئيسية، ابحث عن أعلى ترتيب في الفئات الرئيسية
        const mainCats = await getMainCategories();
        if (mainCats.length > 0) {
          // البحث عن أعلى ترتيب موجود وإضافة 1
          const maxOrder = Math.max(...mainCats.map(cat => cat.order || 0));
          newOrder = maxOrder + 1;
        }
      }
    } catch (orderErr) {
      console.warn('Error determining category order:', orderErr);
      // في حالة الخطأ، استخدم 0 أو القيمة الأصلية
      newOrder = category.order || 0;
    }

    // تحويل البيانات لتتوافق مع النموذج
    const categoryData = {
      ...category,
      name: category.name || '',
      description: category.description || '',
      parentId: category.parentId || null,
      imageUrl: category.imageUrl || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
      order: newOrder, // استخدام الترتيب الجديد
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // إضافة الفئة إلى قاعدة البيانات
    const docRef = await addDoc(categoriesCollection, categoryData);

    // إنشاء كائن الفئة الكامل مع المعرف وتواريخ الإنشاء والتحديث
    const newCategory = {
      id: docRef.id,
      ...categoryData,
      createdAt: new Date(),
      updatedAt: new Date()
    } as Category;

    // حفظ البيانات محليًا إذا كان التخزين المحلي متاحًا
    try {
      if (typeof localStorage !== 'undefined') {
        // 1. تحديث كاش جميع الفئات
        let categories = [];
        const categoriesCache = localStorage.getItem('categories');

        if (categoriesCache) {
          try {
            categories = JSON.parse(categoriesCache);
          } catch (parseError) {
            console.warn('Error parsing categories cache:', parseError);
            categories = [];
          }
        } else {
          // إذا لم يكن هناك تخزين مؤقت، قم بإنشاء واحد جديد
          try {
            const allCats = await getAllCategories(true);
            categories = allCats;
          } catch (fetchError) {
            console.warn('Error fetching categories for cache:', fetchError);
            categories = [];
          }
        }

        // إضافة الفئة الجديدة إلى التخزين المؤقت
        categories.push(newCategory);
        localStorage.setItem('categories', JSON.stringify(categories));

        // 2. تحديث كاش الفئات الرئيسية إذا كانت فئة رئيسية
        if (!category.parentId) {
          let mainCategories = [];
          const mainCategoriesCache = localStorage.getItem('main_categories');

          if (mainCategoriesCache) {
            try {
              mainCategories = JSON.parse(mainCategoriesCache);
            } catch (parseError) {
              console.warn('Error parsing main categories cache:', parseError);
              mainCategories = [];
            }
          }

          // إضافة الفئة الرئيسية الجديدة إلى التخزين المؤقت
          mainCategories.push(newCategory);
          localStorage.setItem('main_categories', JSON.stringify(mainCategories));
        }

        // 3. تحديث كاش الفئات الفرعية إذا كانت فئة فرعية
        if (category.parentId) {
          const subcategoriesKey = `subcategories_${category.parentId}`;
          let subcategories = [];
          const subcategoriesCache = localStorage.getItem(subcategoriesKey);

          if (subcategoriesCache) {
            try {
              subcategories = JSON.parse(subcategoriesCache);
            } catch (parseError) {
              console.warn(`Error parsing subcategories cache for ${category.parentId}:`, parseError);
              subcategories = [];
            }
          }

          // إضافة الفئة الفرعية الجديدة إلى التخزين المؤقت
          subcategories.push({
            ...newCategory,
            parentId: category.parentId,
            categoryId: category.parentId
          } as SubCategory);
          localStorage.setItem(subcategoriesKey, JSON.stringify(subcategories));
        }
      }
    } catch (localStorageError) {
      console.warn('Error accessing localStorage:', localStorageError);
      // استمر في التنفيذ حتى لو فشل التخزين المحلي
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding category:', error);
    // يمكن إضافة معالجة خاصة للعمل في حالة عدم الاتصال
    if ((error as any).code === 'unavailable') {
      console.log('Firestore is currently unavailable, using offline mode');

      // إنشاء معرف مؤقت
      const tempId = `temp_${Date.now()}`;

      // إنشاء كائن الفئة الكامل
      const offlineCategory = {
        id: tempId,
        ...category,
        createdAt: new Date(),
        updatedAt: new Date(),
        _offline: true
      };

      // حفظ البيانات محليًا إذا كان التخزين المحلي متاحًا
      try {
        if (typeof localStorage !== 'undefined') {
          // حفظ في مجموعة الفئات غير المتصلة
          const offlineCategories = localStorage.getItem('offline_categories') || '[]';
          const categories = JSON.parse(offlineCategories);
          categories.push(offlineCategory);
          localStorage.setItem('offline_categories', JSON.stringify(categories));

          // إضافة إلى التخزين المؤقت العادي أيضًا لضمان ظهورها في الواجهة
          const allCategoriesCache = localStorage.getItem('categories') || '[]';
          const allCategories = JSON.parse(allCategoriesCache);
          allCategories.push(offlineCategory);
          localStorage.setItem('categories', JSON.stringify(allCategories));

          // إذا كانت فئة رئيسية، إضافتها إلى كاش الفئات الرئيسية
          if (!category.parentId) {
            const mainCategoriesCache = localStorage.getItem('main_categories') || '[]';
            const mainCategories = JSON.parse(mainCategoriesCache);
            mainCategories.push(offlineCategory);
            localStorage.setItem('main_categories', JSON.stringify(mainCategories));
          }

          return tempId;
        }
      } catch (localError) {
        console.error('Error saving category offline:', localError);
      }
    }

    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('فشل في إضافة فئة جديدة');
    }
  }
};

// الحصول على كافة الفئات الرئيسية
export const getMainCategories = async (forceRefresh?: boolean): Promise<Category[]> => {
  try {
    // إضافة سجل للتشخيص
    console.log('Loading main categories, forceRefresh:', forceRefresh);

    // لا نستخدم التخزين المؤقت أولاً، بل نحاول دائمًا الحصول على البيانات من قاعدة البيانات أولاً
    // سنستخدم التخزين المؤقت فقط في حالة فشل الاتصال بقاعدة البيانات

    const categoriesCollection = getCategoriesCollection();

    // إنشاء استعلام للحصول على جميع الفئات
    const q = query(
      categoriesCollection,
      orderBy('name')
    );

    // تنفيذ الاستعلام
    const querySnapshot = await getDocs(q);
    let allCategories: Category[] = [];

    // معالجة كل وثيقة وفلترة الفئات الرئيسية فقط
    querySnapshot.forEach(doc => {
      const data = doc.data();

      // التحقق مما إذا كانت فئة رئيسية (لا يوجد لها parentId أو parentId هو null)
      if (!data.parentId) {
        allCategories.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        } as Category);
      }
    });

    // طباعة عدد الفئات الرئيسية التي تم العثور عليها
    console.log(`تم العثور على ${allCategories.length} فئة رئيسية`);

    // إذا لم تكن هناك فئات نشطة، حاول الحصول على جميع الفئات الرئيسية بغض النظر عن حالتها
    if (allCategories.length === 0) {
      const q = query(categoriesCollection, orderBy('name'));
      const querySnapshot = await getDocs(q);

      querySnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
          } as Category;
        })
        .filter(category => !category.parentId) // نرشح فقط الفئات التي ليس لها parentId
        .forEach(category => {
          // إضافة الفئة فقط إذا لم تكن موجودة بالفعل
          if (!allCategories.some(existingCategory => existingCategory.id === category.id)) {
            allCategories.push(category);
          }
        });
    }

    // تحديث التخزين المحلي بالفئات الرئيسية التي تم استرجاعها
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('main_categories', JSON.stringify(allCategories));
      }
    } catch (localStorageError) {
      console.warn('Error updating main categories cache:', localStorageError);
    }

    return allCategories;
  } catch (error) {
    console.error('Error getting main categories:', error);

    // محاولة استرجاع البيانات من التخزين المحلي إذا فشلت عملية الجلب
    try {
      if (typeof localStorage !== 'undefined') {
        const categoriesCache = localStorage.getItem('main_categories');
        if (categoriesCache) {
          console.log('Returning main categories from local cache');
          return JSON.parse(categoriesCache);
        }
      }
    } catch (localStorageError) {
      console.warn('Error reading main categories from cache:', localStorageError);
    }

    // إرجاع مصفوفة فارغة بدلاً من رمي استثناء
    return [];
  }
};

// الحصول على جميع الفئات
export const getAllCategories = async (includeInactive = false): Promise<Category[]> => {
  try {
    if (!db) {
      console.error('Database is not initialized');
      return [];
    }

    const categoriesRef = collection(db, 'categories');
    const q = query(categoriesRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);

    const categories: Category[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      categories.push({
        id: doc.id,
        name: data.name || '',
        description: data.description || '',
        parentId: data.parentId,
        order: data.order,
        isActive: data.isActive === undefined ? true : data.isActive,
        imageUrl: data.imageUrl,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
    });
    });

    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

// الحصول على الفئات الفرعية تحت فئة معينة
export const getSubcategories = async (categoryId: string, forceRefresh: boolean = false): Promise<SubCategory[]> => {
  const MAX_RETRIES = 3;
  let retryCount = 0;

  // وظيفة لمحاولة الحصول على البيانات مع إعادة المحاولة
  const tryFetchSubcategories = async (): Promise<SubCategory[]> => {
    try {
      console.log(`محاولة تحميل الفئات الفرعية للفئة ${categoryId} (محاولة ${retryCount + 1}/${MAX_RETRIES})`);

      if (!categoryId) {
        console.error('تم تقديم معرف فئة فارغ');
        return [];
      }

      // محاولة استخدام التخزين المؤقت أولاً لتحسين الأداء
      try {
        if (typeof localStorage !== 'undefined' && !forceRefresh) {
          const subcategoriesKey = `subcategories_${categoryId}`;
          const cachedData = localStorage.getItem(subcategoriesKey);
          const timestampStr = localStorage.getItem(`${subcategoriesKey}_timestamp`);

          if (cachedData && timestampStr) {
            // التحقق من عمر البيانات المخزنة (أقل من ساعة)
            const timestamp = parseInt(timestampStr, 10);
            const now = Date.now();
            const age = now - timestamp;
            const maxAge = 60 * 60 * 1000; // ساعة واحدة بالملي ثانية

            if (age < maxAge) {
              console.log(`استخدام البيانات المخزنة محليًا للفئات الفرعية (عمر ${Math.round(age / 1000 / 60)} دقيقة)`);
              return JSON.parse(cachedData);
            }
            console.log(`البيانات المخزنة قديمة جدًا (${Math.round(age / 1000 / 60)} دقيقة)`);
          }
        }
      } catch (cacheError) {
        console.warn('خطأ في قراءة البيانات المخزنة محليًا:', cacheError);
      }

      const firestore = ensureFirestore();
      const categoriesCollection = getCategoriesCollection();

      // استعلام للحصول على الفئات الفرعية مرتبة حسب الترتيب
      const q = query(
        categoriesCollection,
        where('parentId', '==', categoryId)
      );

      const querySnapshot = await getDocs(q);
      const subcategories: SubCategory[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data();

        subcategories.push({
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          categoryId: categoryId,
          parentId: data.parentId,
          order: data.order || 0,
          isActive: data.isActive !== undefined ? data.isActive : true,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        });
      });

      console.log(`تم تحميل ${subcategories.length} فئة فرعية للفئة ${categoryId}`);

      // تخزين النتائج في localStorage للوصول السريع في المرة القادمة
      try {
        if (typeof localStorage !== 'undefined') {
          const subcategoriesKey = `subcategories_${categoryId}`;
          localStorage.setItem(subcategoriesKey, JSON.stringify(subcategories));
          localStorage.setItem(`${subcategoriesKey}_timestamp`, Date.now().toString());
        }
      } catch (cacheError) {
        console.warn('خطأ في حفظ الفئات الفرعية في التخزين المحلي:', cacheError);
      }

      return subcategories;
    } catch (error) {
      console.error(`خطأ في تحميل الفئات الفرعية (محاولة ${retryCount + 1}/${MAX_RETRIES}):`, error);

      // إذا كان الخطأ متعلق بالاتصال، حاول استخدام النسخة المخزنة محليًا
      if ((error as any)?.code === 'unavailable' || (error as any)?.name === 'FirebaseError') {
    try {
      if (typeof localStorage !== 'undefined') {
            const subcategoriesKey = `subcategories_${categoryId}`;
            const cachedData = localStorage.getItem(subcategoriesKey);

            if (cachedData) {
              console.log('استخدام البيانات المخزنة محليًا للفئات الفرعية');
              return JSON.parse(cachedData);
        }
      }
    } catch (localStorageError) {
          console.warn('خطأ في قراءة البيانات المخزنة محليًا:', localStorageError);
        }
      }

      // إذا استنفدنا جميع المحاولات، ارمي الخطأ لمعالجته في المستوى الأعلى
      if (retryCount >= MAX_RETRIES - 1) {
        throw error;
      }

      // إعادة المحاولة بعد تأخير
      retryCount++;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return tryFetchSubcategories();
    }
  };

  try {
    return await tryFetchSubcategories();
  } catch (error) {
    console.error('فشل في الحصول على الفئات الفرعية بعد عدة محاولات:', error);

    // محاولة أخيرة للحصول على البيانات المخزنة محليًا
    try {
      if (typeof localStorage !== 'undefined') {
        const subcategoriesKey = `subcategories_${categoryId}`;
        const cachedData = localStorage.getItem(subcategoriesKey);

        if (cachedData) {
          console.log('استخدام البيانات المخزنة محليًا كملاذ أخير');
          return JSON.parse(cachedData);
        }
      }
    } catch (localStorageError) {
      console.warn('خطأ في قراءة البيانات المخزنة محليًا:', localStorageError);
    }

    // إذا وصلنا إلى هنا، نعيد مصفوفة فارغة بدلاً من رمي الخطأ
    return [];
  }
};

// تحديث فئة موجودة
export const updateCategory = async (categoryId: string, categoryData: Partial<Category>): Promise<Category> => {
  try {
    if (!db) {
      console.error('Database is not initialized');
      throw new Error('Database is not initialized');
    }

    // إضافة طابع زمني للتحديث
    const updatedData = {
      ...categoryData,
      updatedAt: typeof categoryData.updatedAt === 'string'
        ? new Date(categoryData.updatedAt)
        : (categoryData.updatedAt || new Date())
    };

    // تحديث الفئة في Firestore
    const categoryRef = doc(db, 'categories', categoryId);
    await updateDoc(categoryRef, updatedData);

    // الحصول على البيانات المحدثة
    const updatedSnapshot = await getDoc(categoryRef);

    if (!updatedSnapshot.exists()) {
      throw new Error(`Category with ID ${categoryId} not found after update`);
    }

    const data = updatedSnapshot.data();

    const updatedCategory: Category = {
      id: categoryId,
      name: data.name || '',
      description: data.description || '',
      parentId: data.parentId,
      order: data.order,
      isActive: data.isActive === undefined ? true : data.isActive,
      imageUrl: data.imageUrl,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
    };

    return updatedCategory;
  } catch (error) {
    console.error('Error updating category:', error);
      throw error;
  }
};

// حذف فئة
export const deleteCategory = async (id: string): Promise<void> => {
  try {
    console.log(`محاولة حذف الفئة بمعرف: ${id}`);

    // الحصول على معلومات الفئة للتشخيص
    const category = await getCategoryById(id);
    if (!category) {
      console.error(`لم يتم العثور على الفئة بمعرف: ${id}`);
      throw new Error('لم يتم العثور على الفئة');
    }

    console.log(`الفئة المراد حذفها: ${category.name}, فئة فرعية: ${category.parentId ? 'نعم' : 'لا'}`);

    // التحقق من وجود فئات فرعية (فقط للفئات الرئيسية)
    if (!category.parentId) {
      const subcategories = await getSubcategories(id);
      if (subcategories.length > 0) {
        console.error(`لا يمكن حذف الفئة لأنها تحتوي على ${subcategories.length} فئة فرعية`);
        throw new Error('لا يمكن حذف الفئة لأنها تحتوي على فئات فرعية');
      }
    }

    // التحقق من وجود منتجات مرتبطة بالفئة
    try {
      const productsExist = await checkCategoryHasProducts(id);
      if (productsExist) {
        console.error(`لا يمكن حذف الفئة لأنها تحتوي على منتجات`);
        throw new Error('لا يمكن حذف الفئة لأنها تحتوي على منتجات');
      }
    } catch (checkError) {
      console.error('Error checking if category has products:', checkError);
      throw new Error('فشل في التحقق من العناصر المرتبطة بالفئة');
    }

    // حذف الفئة من Firestore
    const firestore = ensureFirestore();
    const docRef = doc(firestore, 'categories', id);
    await deleteDoc(docRef);
    console.log(`تم حذف الفئة بنجاح من Firestore: ${id}`);

    // تحديث التخزين المحلي
    try {
      if (typeof localStorage !== 'undefined') {
        // 1. تحديث كاش جميع الفئات
        const categoriesCache = localStorage.getItem('categories');
        if (categoriesCache) {
          const categories = JSON.parse(categoriesCache);
          const filteredCategories = categories.filter((c: Category) => c.id !== id);
          localStorage.setItem('categories', JSON.stringify(filteredCategories));
          console.log(`تم تحديث كاش جميع الفئات`);
        }

        // 2. تحديث كاش الفئات الرئيسية إذا كانت فئة رئيسية
        if (!category.parentId) {
          const mainCategoriesCache = localStorage.getItem('main_categories');
          if (mainCategoriesCache) {
            const mainCategories = JSON.parse(mainCategoriesCache);
            const filteredMainCategories = mainCategories.filter((c: Category) => c.id !== id);
            localStorage.setItem('main_categories', JSON.stringify(filteredMainCategories));
            console.log(`تم تحديث كاش الفئات الرئيسية`);
          }
        }

        // 3. تحديث كاش الفئات الفرعية إذا كانت فئة فرعية
        if (category.parentId) {
          const subcategoriesKey = `subcategories_${category.parentId}`;
          const subcategoriesCache = localStorage.getItem(subcategoriesKey);
          if (subcategoriesCache) {
            const subcategories = JSON.parse(subcategoriesCache);
            const filteredSubcategories = subcategories.filter((c: SubCategory) => c.id !== id);
            localStorage.setItem(subcategoriesKey, JSON.stringify(filteredSubcategories));
            console.log(`تم تحديث كاش الفئات الفرعية للفئة الرئيسية ${category.parentId}`);
          }
        }

        // 4. حذف كاش الفئات الفرعية للفئة المحذوفة إذا كانت فئة رئيسية
        if (!category.parentId) {
          const subcategoriesKey = `subcategories_${id}`;
          localStorage.removeItem(subcategoriesKey);
          localStorage.removeItem(`${subcategoriesKey}_timestamp`);
          console.log(`تم حذف كاش الفئات الفرعية للفئة المحذوفة`);
        }
      }
    } catch (localError) {
      console.warn('Error updating local categories after delete:', localError);
      // استمر في التنفيذ حتى إذا فشل تحديث التخزين المحلي
    }

    console.log(`تم حذف الفئة بنجاح: ${category.name}`);
  } catch (error) {
    console.error('Error deleting category:', error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('فشل في حذف الفئة');
    }
  }
};

// التحقق من وجود منتجات في فئة
export const checkCategoryHasProducts = async (categoryId: string): Promise<boolean> => {
  try {
    const firestore = ensureFirestore();
    const inventoryQuery = query(
      collection(firestore, 'inventory'),
      where('categoryId', '==', categoryId)
    );

    const querySnapshot = await getDocs(inventoryQuery);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking category products:', error);
    throw error;
  }
};

// الحصول على فئة بواسطة المعرف
export const getCategoryById = async (id: string): Promise<Category | null> => {
  try {
    const firestore = ensureFirestore();
    const docRef = doc(firestore, 'categories', id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as Category;
    }

    return null;
  } catch (error) {
    console.error('Error getting category:', error);
    throw new Error('فشل في الحصول على الفئة');
  }
};

// تحريك الفئة لأعلى أو أسفل
export const moveCategoryPosition = async (categoryId: string, direction: 'up' | 'down'): Promise<void> => {
  try {
    console.log(`جاري محاولة تحريك الفئة ${categoryId} إلى ${direction === 'up' ? 'أعلى' : 'أسفل'}`);

    // الحصول على الفئة المراد تحريكها
    const category = await getCategoryById(categoryId);
    if (!category) {
      console.error(`الفئة ${categoryId} غير موجودة`);
      throw new Error('الفئة غير موجودة');
    }

    console.log(`الفئة المحددة: ${category.name}, الترتيب الحالي: ${category.order}`);

    // الحصول على قائمة الفئات في نفس المستوى
    let siblingCategories: Category[];
    if (category.parentId) {
      // الفئة فرعية، نحصل على كل الفئات الفرعية تحت نفس الفئة الرئيسية
      console.log(`الحصول على الفئات الفرعية تحت الفئة الرئيسية ${category.parentId}`);
      siblingCategories = await getSubcategories(category.parentId);
    } else {
      // الفئة رئيسية، نحصل على كل الفئات الرئيسية
      console.log(`الحصول على الفئات الرئيسية`);
      siblingCategories = await getMainCategories(true); // تمرير true لتحديث من قاعدة البيانات
    }

    console.log(`تم العثور على ${siblingCategories.length} فئة في نفس المستوى`);

    // ترتيب الفئات بناءً على الترتيب الحالي
    siblingCategories.sort((a, b) => (a.order || 0) - (b.order || 0));

    console.log("ترتيب الفئات الحالي:", siblingCategories.map(c => `${c.name} (${c.order})`));

    // البحث عن موقع الفئة الحالية
    const currentIndex = siblingCategories.findIndex(c => c.id === categoryId);
    if (currentIndex === -1) {
      console.error(`لم يتم العثور على الفئة ${categoryId} في القائمة`);
      throw new Error('لم يتم العثور على الفئة في القائمة');
    }

    let swapIndex: number;
    if (direction === 'up') {
      // تحريك لأعلى (ترتيب أقل)
      swapIndex = currentIndex - 1;
      if (swapIndex < 0) {
        console.error(`الفئة ${category.name} موجودة بالفعل في أعلى القائمة`);
        throw new Error('الفئة موجودة بالفعل في أعلى القائمة');
      }
    } else {
      // تحريك لأسفل (ترتيب أكبر)
      swapIndex = currentIndex + 1;
      if (swapIndex >= siblingCategories.length) {
        console.error(`الفئة ${category.name} موجودة بالفعل في أسفل القائمة`);
        throw new Error('الفئة موجودة بالفعل في أسفل القائمة');
      }
    }

    console.log(`مبادلة الفئة "${siblingCategories[currentIndex].name}" (${siblingCategories[currentIndex].order}) مع "${siblingCategories[swapIndex].name}" (${siblingCategories[swapIndex].order})`);

    // تحديث الترتيب المباشر فقط للفئتين المتأثرتين
    const current = siblingCategories[currentIndex];
    const swap = siblingCategories[swapIndex];

    // تبديل الترتيب بشكل مباشر
    const currentOrder = current.order || 0;
    const swapOrder = swap.order || 0;

    console.log(`تغيير ترتيب ${current.name} من ${currentOrder} إلى ${swapOrder}`);
    console.log(`تغيير ترتيب ${swap.name} من ${swapOrder} إلى ${currentOrder}`);

    // تنفيذ التحديثات لقاعدة البيانات
    const db = ensureFirestore();

    // تحديث الفئة الأولى
    await updateDoc(doc(db, 'categories', current.id), {
      order: swapOrder,
      updatedAt: serverTimestamp()
    });

    // تحديث الفئة الثانية
    await updateDoc(doc(db, 'categories', swap.id), {
      order: currentOrder,
      updatedAt: serverTimestamp()
    });

    console.log(`تم تحريك الفئة "${current.name}" في اتجاه ${direction === 'up' ? 'لأعلى' : 'لأسفل'} بنجاح`);

    return;
  } catch (error) {
    console.error('Error moving category:', error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('فشل في تحريك الفئة');
    }
  }
};

// تحويل فئة رئيسية إلى فئة فرعية
export const convertToSubcategory = async (categoryId: string, parentId: string): Promise<void> => {
  try {
    // التحقق من أن الفئة موجودة
    const category = await getCategoryById(categoryId);
    if (!category) {
      throw new Error('الفئة غير موجودة');
    }

    // التحقق من أن الفئة الرئيسية موجودة
    const parentCategory = await getCategoryById(parentId);
    if (!parentCategory) {
      throw new Error('الفئة الرئيسية غير موجودة');
    }

    // التحقق من عدم وجود تداخل (الفئة الأب ليست فرعية من الفئة الحالية)
    if (category.id === parentId) {
      throw new Error('لا يمكن جعل الفئة فرعية من نفسها');
    }

    // التحقق من أن الفئة الأب هي فئة رئيسية
    if (parentCategory.parentId) {
      throw new Error('لا يمكن تحويل الفئة إلى فئة فرعية تحت فئة فرعية أخرى');
    }

    // الحصول على الفئات الفرعية للفئة الحالية (إذا كانت فئة رئيسية)
    const subcategories = category.parentId ? [] : await getSubcategories(category.id);

    if (subcategories.length > 0) {
      throw new Error('لا يمكن تحويل الفئة إلى فئة فرعية لأنها تحتوي على فئات فرعية');
    }

    // تحويل الفئة إلى فئة فرعية باستخدام Firestore مباشرة
    const firestore = ensureFirestore();
    const docRef = doc(firestore, 'categories', categoryId);

    // تحديث الفئة مع parentId جديد
    await updateDoc(docRef, {
      parentId: parentId,
      updatedAt: serverTimestamp()
    });

    // إعادة الحصول على الفئة بعد التحديث
    const updatedCategory = await getCategoryById(categoryId);
    if (!updatedCategory) {
      throw new Error('فشل في العثور على الفئة بعد التحديث');
    }

    console.log('تم تحويل الفئة إلى فئة فرعية بنجاح:', updatedCategory);

    // تحديث التخزين المحلي
    try {
      if (typeof localStorage !== 'undefined') {
        // 1. تحديث كاش جميع الفئات
        const allCategoriesCache = localStorage.getItem('categories');
        if (allCategoriesCache) {
          const allCategories = JSON.parse(allCategoriesCache);
          const categoryIndex = allCategories.findIndex((c: Category) => c.id === categoryId);

          if (categoryIndex !== -1) {
            // تحديث الفئة لتصبح فرعية
            allCategories[categoryIndex] = {
              ...allCategories[categoryIndex],
              parentId: parentId,
              updatedAt: new Date()
            };
            localStorage.setItem('categories', JSON.stringify(allCategories));
          }
        }

        // 2. إزالة الفئة من كاش الفئات الرئيسية
        const mainCategoriesCache = localStorage.getItem('main_categories');
        if (mainCategoriesCache) {
          const mainCategories = JSON.parse(mainCategoriesCache);
          const updatedMainCategories = mainCategories.filter((c: Category) => c.id !== categoryId);
          localStorage.setItem('main_categories', JSON.stringify(updatedMainCategories));
        }

        // 3. تحديث كاش الفئات الفرعية للفئة الأب الجديدة
        const subcategoriesKey = `subcategories_${parentId}`;
        const subcategoriesCache = localStorage.getItem(subcategoriesKey);

        if (subcategoriesCache) {
          // إضافة الفئة إلى الفئات الفرعية الموجودة
          const subcategories = JSON.parse(subcategoriesCache);
          if (!subcategories.some((c: SubCategory) => c.id === categoryId)) {
            subcategories.push({
              ...updatedCategory,
              parentId: parentId,
              categoryId: parentId
            } as SubCategory);
            localStorage.setItem(subcategoriesKey, JSON.stringify(subcategories));
          }
        } else {
          // إنشاء كاش جديد للفئات الفرعية
          const newSubcategories = [{
            ...updatedCategory,
            parentId: parentId,
            categoryId: parentId
          } as SubCategory];
          localStorage.setItem(subcategoriesKey, JSON.stringify(newSubcategories));
        }

        // 4. إعادة تحميل الفئات الفرعية للفئة الأب
        await getSubcategories(parentId);
      }
    } catch (localStorageError) {
      console.warn('Error updating localStorage after conversion to subcategory:', localStorageError);
      // استمر في التنفيذ حتى إذا فشل تحديث التخزين المحلي
    }

    return;
  } catch (error) {
    console.error('Error converting category to subcategory:', error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('فشل في تحويل الفئة إلى فئة فرعية');
    }
  }
};

// تحويل فئة فرعية إلى فئة رئيسية
export const convertToMainCategory = async (categoryId: string): Promise<void> => {
  try {
    // التحقق من أن الفئة موجودة
    const category = await getCategoryById(categoryId);
    if (!category) {
      throw new Error('الفئة غير موجودة');
    }

    // التحقق من أن الفئة فرعية بالفعل
    if (!category.parentId) {
      throw new Error('الفئة هي بالفعل فئة رئيسية');
    }

    // حفظ معرف الفئة الرئيسية السابقة
    const parentCategoryId = category.parentId;

    // تحديث الفئة في Firestore مباشرة
    const firestore = ensureFirestore();
    const docRef = doc(firestore, 'categories', categoryId);

    // استخدام قيمة خالية بدلاً من حذف الحقل
    await updateDoc(docRef, {
      parentId: null,
      updatedAt: serverTimestamp()
    });

    // تحديث التخزين المحلي بشكل مباشر
    try {
      if (typeof localStorage !== 'undefined') {
        // 1. تحديث كاش جميع الفئات
        let allCategories = [];
        const categoriesCache = localStorage.getItem('categories');
        if (categoriesCache) {
          allCategories = JSON.parse(categoriesCache);

          // تحديث الفئة في كاش الفئات العام
          const categoryIndex = allCategories.findIndex((c: Category) => c.id === categoryId);
          if (categoryIndex !== -1) {
            allCategories[categoryIndex] = {
              ...allCategories[categoryIndex],
              parentId: undefined
            };
            localStorage.setItem('categories', JSON.stringify(allCategories));
          }
        }

        // 2. تحديث كاش الفئات الرئيسية
        let mainCategories = [];
        const mainCategoriesCache = localStorage.getItem('main_categories');
        if (mainCategoriesCache) {
          mainCategories = JSON.parse(mainCategoriesCache);
        }

        // الحصول على الفئة المحدثة
        const updatedCategory = await getCategoryById(categoryId);
        if (updatedCategory) {
          // تحديث الفئة في كاش الفئات الرئيسية
          if (!mainCategories.some((c: Category) => c.id === categoryId)) {
            mainCategories.push(updatedCategory);
            localStorage.setItem('main_categories', JSON.stringify(mainCategories));
          }
        }

        // 3. تحديث كاش الفئات الفرعية للفئة الأب السابقة
        if (parentCategoryId) {
          const subcategoriesKey = `subcategories_${parentCategoryId}`;
          const subcategoriesCache = localStorage.getItem(subcategoriesKey);
          if (subcategoriesCache) {
            const subcategories = JSON.parse(subcategoriesCache);
            const updatedSubcategories = subcategories.filter((c: SubCategory) => c.id !== categoryId);
            localStorage.setItem(subcategoriesKey, JSON.stringify(updatedSubcategories));
          }
        }
      }
    } catch (localStorageError) {
      console.warn('Error updating localStorage after conversion:', localStorageError);
    }

    // جلب البيانات المحدثة
    try {
      const updatedMainCategories = await getMainCategories();
      console.log(`تم تحميل ${updatedMainCategories.length} فئة رئيسية بعد التحويل`);
    } catch (reloadError) {
      console.warn('Error reloading categories after conversion:', reloadError);
    }

    return;
  } catch (error) {
    console.error('Error converting subcategory to main category:', error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('فشل في تحويل الفئة إلى فئة رئيسية');
    }
  }
};

// حذف جميع الفئات
export const deleteAllCategories = async (): Promise<void> => {
  try {
    // الحصول على جميع الفئات
    const categoriesCollection = getCategoriesCollection();
    const querySnapshot = await getDocs(categoriesCollection);

    // التحقق من وجود فئات
    if (querySnapshot.empty) {
      console.log('لا توجد فئات لحذفها');
      return;
    }

    console.log(`سيتم حذف ${querySnapshot.size} فئة من قاعدة البيانات...`);

    // حذف الفئة من عناصر المخزون أولاً
    try {
      // للأمان، نجمع أسماء الفئات أولاً قبل الحذف
      const categoryNames = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return data.name as string;
      });

      // حذف الفئات من عناصر المخزون
      for (const categoryName of categoryNames) {
        if (categoryName) {
          try {
            const affectedItems = await clearCategoryFromInventory(categoryName);
            console.log(`تم إزالة الفئة "${categoryName}" من ${affectedItems} عنصر مخزون`);
          } catch (clearError) {
            console.warn(`فشل في إزالة الفئة "${categoryName}" من عناصر المخزون:`, clearError);
          }
        }
      }
    } catch (inventoryUpdateError) {
      console.warn('حدث خطأ أثناء تحديث عناصر المخزون:', inventoryUpdateError);
    }

    // حذف كل فئة على حدة
    const deletePromises = querySnapshot.docs.map(async (doc) => {
      await deleteDoc(doc.ref);
      console.log(`تم حذف الفئة بمعرف: ${doc.id}`);
    });

    // انتظار انتهاء جميع عمليات الحذف
    await Promise.all(deletePromises);

    // مسح التخزين المحلي
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('categories');
        localStorage.removeItem('main_categories');

        // البحث عن وحذف كافة مفاتيح الفئات الفرعية
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('subcategories_')) {
            localStorage.removeItem(key);
          }
        }

        console.log('تم مسح بيانات الفئات من التخزين المحلي');
      }
    } catch (cacheError) {
      console.warn('Error clearing category cache:', cacheError);
    }

    console.log('تم حذف جميع الفئات بنجاح');
  } catch (error) {
    console.error('Error deleting all categories:', error);
    throw new Error('فشل في حذف جميع الفئات');
  }
};